/**
 * <grid-controls>
 *
 * Wraps the FILTERS / count / SORT BY row above a product grid. Filters, sort
 * and pagination are real URLs and every one of them works with JavaScript off.
 * This element adds one convenience — submitting the sort form when the select
 * changes, so a shopper does not have to find an "Apply" button that only
 * exists as the no-JS fallback.
 *
 * The listener is delegated from the element rather than bound to the form,
 * because the row is also what the theme editor re-renders on a setting change
 * and a listener bound to a replaced node would go quiet.
 *
 * Renamed from <product-grid-controls> when the row became a shared snippet:
 * the product grid and the search results page both render it, so neither name
 * may be the file's.
 */

if (!customElements.get('grid-controls')) {
  customElements.define(
    'grid-controls',
    class GridControls extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange);
        this.syncPriceFilter(new URL(window.location.href));
        this.syncBrandFilter(new URL(window.location.href));
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      onChange(event) {
        const select = event.target.closest('select[data-auto-submit]');
        if (!select) return;
        select.form?.requestSubmit();
      }

      syncPriceFilter(url) {
        const template = this.querySelector('template[data-price-pill]');
        const list = this.querySelector('.grid-controls__active');
        if (!template || !list) return;

        for (const added of this.querySelectorAll('[data-price-fallback]')) added.remove();

        const min = url.searchParams.get('filter.v.price.gte') ?? '';
        const max = url.searchParams.get('filter.v.price.lte') ?? '';

        if (min || max) {
          const money = new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: template.dataset.currency,
          });
          const range = max ? `${money.format(Number(min))} – ${money.format(Number(max))}` : `${money.format(Number(min))}+`;
          const label = `${template.dataset.label}: ${range}`;

          const removeUrl = new URL(url);
          for (const name of ['filter.v.price.gte', 'filter.v.price.lte', 'page']) removeUrl.searchParams.delete(name);

          const pill = template.content.firstElementChild.cloneNode(true);
          pill.querySelector('a').href = `${removeUrl.pathname}${removeUrl.search}`;
          pill.querySelector('[data-price-pill-label]').textContent = label;
          const removeText = pill.querySelector('[data-price-pill-remove]');
          removeText.textContent = removeText.textContent.replace('[[label]]', label);
          list.append(pill);

          const form = this.querySelector('.grid-controls__sort');
          for (const [name, value] of [['filter.v.price.gte', min], ['filter.v.price.lte', max]]) {
            if (!form || !value) continue;

            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            input.dataset.priceFallback = '';
            form.prepend(input);
          }
        }

        list.hidden = list.children.length === 0;
      }

      /**
       * A pill and a sort-form input for every brand `url` applies that
       * Shopify left out. The drawer's brands are a fixed list of tags
       * (component-brand-values.liquid), and Shopify reports a tag as applied
       * only when it is one of the 100 its facet returns — so for most brands
       * Liquid draws no pill to remove it by, and the sort form, which carries
       * the applied facets as hidden inputs, would drop it on the next sort.
       * A brand Shopify did report already has both, marked `data-brand-tag`.
       *
       * A brand with several spellings is one pill however many of them the
       * URL carries, and removing it drops every spelling: the URL Shopify
       * gives a reported pill drops only the spelling it reported, so that
       * pill's link is pointed at the full removal too. The sort form gets an
       * input for each applied spelling it does not already carry.
       *
       * @param {URL} url
       */
      syncBrandFilter(url) {
        const template = this.querySelector('template[data-brand-pill]');
        const list = this.querySelector('.grid-controls__active');
        if (!template || !list) return;

        for (const added of this.querySelectorAll('[data-brand-fallback]')) added.remove();

        let brands = {};
        try {
          brands = JSON.parse(template.dataset.brands ?? '{}');
        } catch (error) {
          console.error(error);
          return;
        }

        /* Repeated values, or comma-joined ones from a hand-typed URL. */
        const applied = url.searchParams.getAll('filter.p.tag').flatMap((value) => value.split(','));
        const form = this.querySelector('.grid-controls__sort');
        const carried = form ? Array.from(form.querySelectorAll('input[name="filter.p.tag"]')).map((input) => input.value) : [];

        for (const [tags, name] of Object.entries(brands)) {
          const spellings = tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);
          const hits = spellings.filter((tag) => applied.includes(tag));
          if (hits.length === 0) continue;

          /* Every other tag kept. */
          const removeUrl = new URL(url);
          const others = applied.filter((value) => !spellings.includes(value));
          removeUrl.searchParams.delete('filter.p.tag');
          for (const value of others) removeUrl.searchParams.append('filter.p.tag', value);
          removeUrl.searchParams.delete('page');
          const removeHref = `${removeUrl.pathname}${removeUrl.search}`;

          const reported = spellings.map((tag) => list.querySelector(`[data-brand-tag="${CSS.escape(tag)}"] a`)).find(Boolean);
          if (reported) {
            reported.href = removeHref;
          } else {
            const label = `${template.dataset.label}: ${name}`;
            const pill = template.content.firstElementChild.cloneNode(true);
            pill.querySelector('a').href = removeHref;
            pill.querySelector('[data-brand-pill-label]').textContent = label;
            const removeText = pill.querySelector('[data-brand-pill-remove]');
            removeText.textContent = removeText.textContent.replace('[[label]]', label);
            list.append(pill);
          }

          if (!form) continue;
          for (const tag of hits) {
            if (carried.includes(tag)) continue;

            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'filter.p.tag';
            input.value = tag;
            input.dataset.brandFallback = '';
            form.prepend(input);
          }
        }

        list.hidden = list.children.length === 0;
      }
    },
  );
}
